import { useEffect, useMemo, useState } from 'react'
import {
  Truck, Map, Radio, LocateFixed, RefreshCw, LayoutDashboard,
  Gauge, GanttChartSquare, Route, Shield, BellRing, Building2, ArrowRight,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import { statusOf } from '../equipment'
import { fmtInt } from '../i18n/format'

// Teto da consulta de ciclos: os números do cartão são "nos últimos N", nunca o total
// do período (a lista vinha paginada e o cartão apresentava o tamanho da página).
const CYCLES_LIMIT = 200

export default function HomeView({ onViewChange }) {
  const { t } = useTranslation()
  const { devices, pendingDevices, feed, categories, mines, operators, alarms } = useWS()
  const { mineQS, matchesScope } = useScope()
  const [counts, setCounts] = useState({ cycles: null, tonnage: null, truncated: false, geofences: null, assignments: null, dayTon: null })

  // O escopo de mina do seletor logo acima vale aqui também: sem ele os cartões
  // mostravam a frota inteira sob o rótulo de uma mina só.
  useEffect(() => {
    const ctrl = new AbortController()
    const set = (patch) => { if (!ctrl.signal.aborted) setCounts(c => ({ ...c, ...patch })) }
    const qs = (base) => (mineQS ? `${base}${base.includes('?') ? '&' : '?'}${mineQS}` : base)
    const get = (url) => fetch(url, { signal: ctrl.signal }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })

    get(qs(`/api/cycles?limit=${CYCLES_LIMIT}`)).then(d => {
      const page = Array.isArray(d) ? d : []
      // O ciclo parcial do fim de turno (isFinal, VMC 0.8) não conta em nada.
      const list = page.filter(c => c?.isFinal !== true)
      set({ cycles: list.length, truncated: page.length >= CYCLES_LIMIT, tonnage: list.reduce((a, c) => a + (c.tonnage || 0), 0) })
    }).catch(() => {})
    get('/api/geofences').then(d => set({ geofences: (d || []).length })).catch(() => {})
    get('/api/assignments').then(d => set({ assignments: (d || []).length })).catch(() => {})
    get(qs('/api/dashboard')).then(d => set({ dayTon: d?.realized?.total })).catch(() => {})
    return () => ctrl.abort()
  }, [mineQS])

  const scoped = useMemo(() => devices.filter(matchesScope), [devices, matchesScope])
  const opCount = useMemo(() => scoped.filter(d => statusOf(d).key === 'op').length, [scoped])
  const withPos = useMemo(() => scoped.filter(d => d.lastLat != null && d.lastLon != null).length, [scoped])
  const unacked = (alarms || []).filter(a => !a.acknowledged).length

  const MODULES = [
    { id: 'equipamentos', label: t('nav:menu.items.equipamentos'), icon: Truck, accent: 'text-brand-600 dark:text-brand-300 bg-brand-50 dark:bg-brand-500/10',
      stats: [t('ops:devices.equipment_count', { count: scoped.length }), t('ops:devices.in_operation', { count: opCount }), pendingDevices.length ? t('ops:home.pending_count', { count: pendingDevices.length }) : null] },
    { id: 'controlador', label: t('nav:menu.items.controlador'), icon: Map, accent: 'text-sky-600 dark:text-sky-300 bg-sky-50 dark:bg-sky-500/10',
      stats: [t('ops:home.on_map', { count: withPos }), t('ops:home.live_tracking')] },
    { id: 'tracking', label: t('nav:menu.items.tracking'), icon: LocateFixed, accent: 'text-teal-600 dark:text-teal-300 bg-teal-50 dark:bg-teal-500/10',
      stats: [t('ops:home.operating_count', { count: opCount }), t('ops:home.cycle_state')] },
    { id: 'ciclos', label: t('nav:menu.items.ciclos'), icon: RefreshCw, accent: 'text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10',
      stats: [
        counts.cycles == null ? t('ops:home.cycles_done')
          : counts.truncated ? t('ops:home.cycles_last', { value: fmtInt(counts.cycles) })
          : t('ops:home.cycles_count', { count: counts.cycles, value: fmtInt(counts.cycles) }),
        counts.tonnage ? t(counts.truncated ? 'ops:home.tons_moved_last' : 'ops:home.tons_moved', { value: fmtInt(counts.tonnage) }) : null,
      ] },
    { id: 'dashboard', label: t('nav:menu.items.dashboard'), icon: LayoutDashboard, accent: 'text-violet-600 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/10',
      stats: [counts.dayTon != null ? t('ops:home.tons_today', { value: fmtInt(counts.dayTon) }) : t('ops:home.realized_vs_target'), t('ops:home.shift_pace')] },
    { id: 'produtividade', label: t('nav:menu.items.produtividade'), icon: Gauge, accent: 'text-emerald-600 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10',
      stats: [t('ops:home.availability_use'), t('ops:home.gmg_standard')] },
    { id: 'timeline', label: t('nav:menu.items.timeline'), icon: GanttChartSquare, accent: 'text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10',
      stats: [t('ops:home.states_per_shift'), t('ops:home.gantt_view')] },
    { id: 'despacho', label: t('nav:menu.items.despacho'), icon: Route, accent: 'text-cyan-600 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-500/10',
      stats: [counts.assignments != null ? t('ops:home.assigned_count', { count: counts.assignments }) : t('ops:home.fleet_allocation'), t('ops:home.front_route_material')] },
    { id: 'geofences', label: t('nav:menu.items.geofences'), icon: Shield, accent: 'text-brand-600 dark:text-brand-300 bg-brand-50 dark:bg-brand-500/10',
      stats: [counts.geofences != null ? t('ops:home.fences_count', { count: counts.geofences }) : t('ops:home.fences_on_map'), t('ops:home.alarm_rules')] },
    { id: 'alarmes', label: t('nav:menu.items.alarmes'), icon: BellRing, accent: unacked ? 'text-status-down bg-red-50 dark:bg-red-500/10' : 'text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800',
      stats: [unacked ? t('ops:home.unacked_count', { count: unacked }) : t('ops:home.no_active_alarms'), t('ops:home.geofence_violations')], alert: unacked > 0 },
    { id: 'eventos', label: t('nav:menu.items.eventos'), icon: Radio, accent: 'text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-500/10',
      stats: [t('ops:home.in_feed', { count: feed.length }), t('ops:home.telemetry_commands')] },
    { id: 'admin-frota', label: t('nav:menu.groups.administration'), icon: Building2, accent: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800',
      stats: [t('ops:home.types_mines', { types: categories.length, mines: mines.length }), t('ops:home.operators_count', { count: operators.length })] },
  ]

  return (
    <div className="h-full overflow-y-auto scrollbar-thin bg-offwhite dark:bg-slate-950">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">{t('ops:home.title')}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('ops:home.subtitle')}</p>
        </div>

        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {MODULES.map(m => {
            const Icon = m.icon
            const stats = m.stats.filter(Boolean)
            return (
              <button
                key={m.id}
                onClick={() => onViewChange(m.id)}
                className={`group text-left bg-white dark:bg-slate-900 rounded-xl border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-5 flex flex-col gap-3 ${m.alert ? 'border-status-down/40' : 'border-slate-200 dark:border-slate-700'}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`w-11 h-11 rounded-lg flex items-center justify-center ${m.accent}`}>
                    <Icon size={20} />
                  </span>
                  <ArrowRight size={16} className="text-slate-300 dark:text-slate-600 group-hover:text-brand-600 transition-colors" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-100">{m.label}</p>
                  <div className="mt-1 space-y-0.5">
                    {stats.map((s, i) => (
                      <p key={i} className={`text-xs ${i === 0 && m.alert ? 'text-status-down font-medium' : 'text-slate-500 dark:text-slate-400'}`}>{s}</p>
                    ))}
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
